import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X } from 'lucide-react';
import { EchobotProps, EchobotAnimation, EyePosition } from '../../types/echobot';

const Echobot: React.FC<EchobotProps> = ({
  initialMessage,
  initialAnimation = 'none',
  context,
  onActionClick,
  showOnMount = false,
  enableMouseTracking = true
}) => {
  const [message, setMessage] = useState(initialMessage || '');
  const [isMessageVisible, setIsMessageVisible] = useState(Boolean(showOnMount && initialMessage?.trim()));
  const [currentAnimation, setCurrentAnimation] = useState<EchobotAnimation>(initialAnimation);
  const [eyePosition, setEyePosition] = useState<EyePosition>({ x: 0, y: 0 });
  const [headRotation, setHeadRotation] = useState(0);
  
  const robotRef = useRef<HTMLDivElement>(null);
  const animationTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const messageTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Mouse tracking for eye movement
  useEffect(() => {
    if (!enableMouseTracking) return;

    let rafId: number | null = null;
    let lastMouseX = 0;
    let lastMouseY = 0;

    const handleMouseMove = (e: MouseEvent) => {
      lastMouseX = e.clientX;
      lastMouseY = e.clientY;

      if (rafId === null) {
        rafId = requestAnimationFrame(() => {
          if (!robotRef.current) {
            rafId = null;
            return;
          }

          const rect = robotRef.current.getBoundingClientRect();
          const robotCenterX = rect.left + rect.width / 2;
          const robotCenterY = rect.top + rect.height / 2;

          const deltaX = lastMouseX - robotCenterX;
          const deltaY = lastMouseY - robotCenterY;

          // Calculate eye movement (limited range)
          const eyeX = Math.max(-5, Math.min(5, deltaX / 30));
          const eyeY = Math.max(-3, Math.min(3, deltaY / 30));

          // Calculate head rotation when mouse is on the left (towards sidebar)
          const isMouseLeft = lastMouseX < window.innerWidth / 3;
          const rotation = isMouseLeft
            ? Math.max(-15, Math.min(15, -deltaX / 50))
            : 0;

          setEyePosition({ x: eyeX, y: eyeY });
          setHeadRotation(rotation);

          rafId = null;
        });
      }
    };

    document.addEventListener('mousemove', handleMouseMove);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
    };
  }, [enableMouseTracking]);

  // Play animation
  const playAnimation = useCallback((animation: EchobotAnimation) => {
    if (animation === 'none') return;

    setCurrentAnimation(animation);

    // Clear previous timeout
    if (animationTimeoutRef.current) {
      clearTimeout(animationTimeoutRef.current);
    }

    // Reset animation after 2 seconds
    animationTimeoutRef.current = setTimeout(() => {
      setCurrentAnimation('none');
    }, 2000);
  }, []);

  // Show message with animation
  const showMessage = useCallback((newMessage: string, animation: EchobotAnimation = 'none', duration: number = 4000) => {
    if (!newMessage.trim()) {
      setMessage('');
      setIsMessageVisible(false);
      return;
    }

    setMessage(newMessage);
    
    // Play animation if specified
    if (animation !== 'none') {
      playAnimation(animation);
    }

    // Show message bubble
    setTimeout(() => {
      setIsMessageVisible(true);
    }, 300);

    // Clear previous timeout
    if (messageTimeoutRef.current) {
      clearTimeout(messageTimeoutRef.current);
    }

    // Auto-hide after duration
    if (duration > 0) {
      messageTimeoutRef.current = setTimeout(() => {
        setIsMessageVisible(false);
      }, duration);
    }
  }, [playAnimation]);

  // Hide message
  const hideMessage = useCallback(() => {
    setIsMessageVisible(false);
  }, []);

  // Handle robot click
  const handleRobotClick = () => {
    if (onActionClick) {
      onActionClick('robot-clicked');
    } else {
      // Default: celebrate animation
      playAnimation('celebrate');
      showMessage('Hallo! 👋 Klick auf mich um zu chatten!', 'none', 3000);
    }
  };

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (animationTimeoutRef.current) {
        clearTimeout(animationTimeoutRef.current);
      }
      if (messageTimeoutRef.current) {
        clearTimeout(messageTimeoutRef.current);
      }
    };
  }, []);

  // Show initial message if provided
  useEffect(() => {
    if (initialMessage?.trim() && showOnMount) {
      showMessage(initialMessage, initialAnimation);
    } else if (!initialMessage?.trim()) {
      setMessage('');
      setIsMessageVisible(false);
    }
  }, [initialMessage, showOnMount, initialAnimation, showMessage]);

  // Expose methods via ref (for external control)
  useEffect(() => {
    if (typeof window !== 'undefined') {
      (window as any).EchobotAPI = {
        showMessage,
        hideMessage,
        playAnimation
      };
    }
  }, [showMessage, hideMessage, playAnimation]);

  return (
    <div className="echobot-container relative inline-block">
      {/* Robot Button */}
      <button
        ref={robotRef}
        className="echobot-robot relative cursor-pointer group"
        onClick={handleRobotClick}
        style={{ animation: 'echobot-float 3s ease-in-out infinite' }}
        title="Echobot"
      >
        {/* Glow ring */}
        <div className="absolute inset-0 rounded-full bg-accent/20 blur-xl scale-125 group-hover:bg-accent/30 transition-all" />

        {/* Main circle */}
        <div className="relative w-14 h-14 rounded-full bg-gradient-to-br from-accent to-info shadow-glow-accent grid place-items-center transition-all duration-300 group-hover:scale-110 group-active:scale-95">
          {/* Robot face SVG */}
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" className="text-txt-inverted">
            {/* Head */}
            <rect x="4" y="4" width="16" height="14" rx="4" fill="currentColor" opacity="0.9" />
            {/* Antenna */}
            <line x1="12" y1="4" x2="12" y2="1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            <circle cx="12" cy="1" r="1.5" fill="currentColor" opacity="0.6" />
            {/* Eyes */}
            <circle cx="9" cy="10" r="1.8" fill="rgb(var(--bg-base))"
              style={{ transform: `translate(${eyePosition.x * 0.3}px, ${eyePosition.y * 0.3}px)` }}
            />
            <circle cx="15" cy="10" r="1.8" fill="rgb(var(--bg-base))"
              style={{ transform: `translate(${eyePosition.x * 0.3}px, ${eyePosition.y * 0.3}px)` }}
            />
            {/* Mouth */}
            <path
              d={currentAnimation === 'celebrate' ? 'M8 14 Q12 17 16 14' : currentAnimation === 'question' ? 'M10 14.5 L14 14.5' : 'M9 14 Q12 16 15 14'}
              stroke="rgb(var(--bg-base))"
              strokeWidth="1.5"
              strokeLinecap="round"
              fill="none"
            />
            {/* Body hint */}
            <rect x="7" y="18" width="10" height="4" rx="2" fill="currentColor" opacity="0.7" />
          </svg>
        </div>

        {/* Notification dot (when message is visible) */}
        {isMessageVisible && (
          <div className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-danger rounded-full border-2 border-bg-base animate-pulse" />
        )}
      </button>

      {/* Speech Bubble */}
      {message.trim() && (
      <div
        className={`absolute bottom-[calc(100%+0.75rem)] right-0 w-[min(20rem,calc(100vw-2rem))] min-w-[180px] rounded-xl border border-accent bg-bg-surface pl-4 pr-8 py-3 text-[13px] font-medium text-txt shadow-glow-accent z-10 transition-all duration-300 ${
          isMessageVisible
            ? 'opacity-100 scale-100 translate-y-0'
            : 'opacity-0 scale-90 translate-y-2.5 pointer-events-none'
        }`}
        style={{ transitionTimingFunction: 'var(--spring-bounce)' }}
      >
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (messageTimeoutRef.current) clearTimeout(messageTimeoutRef.current);
            hideMessage();
          }}
          className="absolute top-1.5 right-1.5 p-0.5 rounded-md text-txt-tertiary hover:text-txt hover:bg-bg-surface-hover transition-colors"
          aria-label="Schließen"
          title="Schließen"
        >
          <X className="w-3.5 h-3.5" />
        </button>
        <span>{message}</span>
        <div className="absolute -bottom-1.5 right-6 h-3 w-3 rotate-45 border-b border-r border-accent bg-bg-surface" />
      </div>
      )}

      <style>{`
        @keyframes echobot-float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-6px); }
        }
      `}</style>
    </div>
  );
};

export default Echobot;
